/**
 * BL-180 Gate 2 — Accounting Connections View
 *
 * Settings panel for managing QuickBooks Online and Xero connections.
 * Both platforms are first-class, equally-weighted connectors.
 *
 * Gate 2 wires the real QBO OAuth redirect.  Xero OAuth is Gate 4.
 *
 * URL parameters handled:
 *   ?qbo_connected=1            — show success banner after callback
 *   ?qbo_error=<code>           — show error banner after failed OAuth
 *   ?qbo_error_description=<s>  — human-readable detail for the error
 */
import { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import {
    ACCOUNTING_PLATFORMS,
    CONNECTION_STATUS,
    PLATFORM_LABELS,
    getConnectionStatusLabel,
    isConnectionActive,
    requiresReconnect,
} from '../../lib/accountingAdapter';
import { buildQboAuthUrl } from '../../lib/qboOAuth';
import { buildXeroAuthUrl, generateCodeVerifier, deriveCodeChallenge } from '../../lib/xeroOAuth';

const DEFAULT_SYNC_ENTITY_TYPES = ['customer', 'invoice', 'payment'];
const ALLOWED_SYNC_ENTITY_TYPES = new Set(DEFAULT_SYNC_ENTITY_TYPES);
const DEFAULT_SYNC_DIRECTION = 'both';

const PLATFORMS = [
    {
        id: ACCOUNTING_PLATFORMS.QBO,
        label: PLATFORM_LABELS[ACCOUNTING_PLATFORMS.QBO],
        description: 'Sync invoices, payments, and customers with QuickBooks Online.',
        logoAlt: 'QuickBooks Online',
    },
    {
        id: ACCOUNTING_PLATFORMS.XERO,
        label: PLATFORM_LABELS[ACCOUNTING_PLATFORMS.XERO],
        description: 'Sync invoices, payments, and contacts with Xero.',
        logoAlt: 'Xero',
    },
];

function AccountingConnectionsView({ theme = 'light' }) {
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [syncingConnectionId, setSyncingConnectionId] = useState(null);
    const [savingConnectionId, setSavingConnectionId] = useState(null);

    const isDark = theme === 'dark';
    const panelClass = isDark
        ? 'bg-gray-800 border-gray-700 text-gray-100'
        : 'bg-white border-gray-200 text-gray-900';
    const mutedClass = isDark ? 'text-gray-400' : 'text-gray-500';
    const labelClass = isDark ? 'text-gray-200' : 'text-gray-700';

    // ── Handle post-OAuth redirect parameters ────────────────────────────────

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('qbo_connected') === '1') {
            setSuccessMessage('QuickBooks Online connected successfully.');
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
        } else if (params.get('xero_connected') === '1') {
            setSuccessMessage('Xero connected successfully.');
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
        } else if (params.get('qbo_error')) {
            const desc = params.get('qbo_error_description');
            setActionError(
                `QuickBooks connection failed: ${params.get('qbo_error')}` +
                (desc ? ` — ${desc}` : '')
            );
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
        } else if (params.get('xero_error')) {
            const desc = params.get('xero_error_description');
            setActionError(
                `Xero connection failed: ${params.get('xero_error')}` +
                (desc ? ` — ${desc}` : '')
            );
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
        }
    }, []);

    // ── Data loading ─────────────────────────────────────────────────────────

    const loadConnections = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data, error: supaErr } = await supabaseClient
                .from('accounting_connections')
                .select('id, platform, display_name, status, last_synced_at, last_sync_error, sync_entity_types, sync_direction, sync_paused');
            if (supaErr) throw supaErr;
            setConnections(data ?? []);
        } catch (err) {
            setError(err.message ?? 'Failed to load accounting connections.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadConnections();
    }, [loadConnections]);

    const connectionForPlatform = (platformId) =>
        connections.find((c) => c.platform === platformId) ?? null;

    const normalizeSyncEntityTypes = useCallback((value) => {
        if (!Array.isArray(value)) return DEFAULT_SYNC_ENTITY_TYPES;
        return value.filter((entityType) => ALLOWED_SYNC_ENTITY_TYPES.has(entityType));
    }, []);

    // ── Action handlers ──────────────────────────────────────────────────────

    const handleConnect = async (platformId) => {
        setActionError(null);
        setSuccessMessage(null);

        if (platformId === ACCOUNTING_PLATFORMS.QBO) {
            // Retrieve the current org_id from Supabase auth so we can embed it
            // in the state parameter and recover it after the OAuth redirect.
            const { data: { user } } = await supabaseClient.auth.getUser();
            const orgId = user?.app_metadata?.org_id ?? user?.user_metadata?.org_id;
            if (!orgId) {
                setActionError('Unable to determine your organisation ID. Please refresh and try again.');
                return;
            }

            const clientId = process.env.REACT_APP_QBO_CLIENT_ID;
            const redirectUri = process.env.REACT_APP_QBO_REDIRECT_URI;

            if (!clientId || !redirectUri) {
                setActionError(
                    'QuickBooks Online credentials are not yet configured. ' +
                    'Set REACT_APP_QBO_CLIENT_ID and REACT_APP_QBO_REDIRECT_URI in your environment. ' +
                    'Provisioning the Intuit Developer app is a separate action outside this build.'
                );
                return;
            }

            // Build a state value that encodes the org_id for CSRF protection.
            // Format: "<orgId>:<random-nonce>"
            const nonce = crypto.randomUUID().replace(/-/g, '').slice(0, 12);
            const state = `${orgId}:${nonce}`;

            // Create (or update) the accounting_connections row in 'pending' state.
            // Store the nonce so the callback can validate the state parameter
            // and reject any request that didn't originate from a real flow.
            await supabaseClient
                .from('accounting_connections')
                .upsert(
                    {
                        org_id: orgId,
                        platform: 'qbo',
                        status: 'pending',
                        display_name: 'QuickBooks Online',
                        oauth_state_nonce: nonce,
                    },
                    { onConflict: 'org_id,platform' }
                );

            let authUrl;
            try {
                authUrl = buildQboAuthUrl({ clientId, redirectUri, state });
            } catch (err) {
                setActionError(err.message);
                return;
            }

            // Redirect to Intuit consent screen.
            window.location.href = authUrl;
            return;
        }

        if (platformId === ACCOUNTING_PLATFORMS.XERO) {
            const { data: { user } } = await supabaseClient.auth.getUser();
            const orgId = user?.app_metadata?.org_id ?? user?.user_metadata?.org_id;
            if (!orgId) {
                setActionError('Unable to determine your organisation ID. Please refresh and try again.');
                return;
            }

            const clientId = process.env.REACT_APP_XERO_CLIENT_ID;
            const redirectUri = process.env.REACT_APP_XERO_REDIRECT_URI;

            if (!clientId || !redirectUri) {
                setActionError(
                    'Xero credentials are not yet configured. ' +
                    'Set REACT_APP_XERO_CLIENT_ID and REACT_APP_XERO_REDIRECT_URI in your environment. ' +
                    'Provisioning the Xero Developer app is a separate action outside this build.'
                );
                return;
            }

            // Generate PKCE code_verifier and derive code_challenge.
            const codeVerifier = generateCodeVerifier();
            let codeChallenge;
            try {
                codeChallenge = await deriveCodeChallenge(codeVerifier);
            } catch (err) {
                setActionError(`Failed to generate PKCE challenge: ${err.message}`);
                return;
            }

            // Build state with nonce for CSRF protection (same pattern as QBO).
            const nonce = crypto.randomUUID().replace(/-/g, '').slice(0, 12);
            const state = `${orgId}:${nonce}`;

            // Store the pending row, nonce, and PKCE code_verifier so the callback
            // Edge Function can validate both and complete the token exchange.
            const { error: upsertErr } = await supabaseClient
                .from('accounting_connections')
                .upsert(
                    {
                        org_id: orgId,
                        platform: 'xero',
                        status: 'pending',
                        display_name: 'Xero',
                        oauth_state_nonce: nonce,
                        xero_pkce_code_verifier: codeVerifier,
                    },
                    { onConflict: 'org_id,platform' }
                );

            if (upsertErr) {
                setActionError(`Failed to initialise Xero connection: ${upsertErr.message}`);
                return;
            }

            let authUrl;
            try {
                authUrl = buildXeroAuthUrl({ clientId, redirectUri, state, codeChallenge });
            } catch (err) {
                setActionError(err.message);
                return;
            }

            // Redirect to Xero consent screen.
            window.location.href = authUrl;
            return;
        }
    };

    const handleDisconnect = async (connection) => {
        setActionError(null);
        try {
            // RLS enforces org_id = auth_org_id() on all UPDATE statements, so
            // cross-org writes are blocked at the database layer.  The .eq('id')
            // filter ensures only this specific connection row is updated.
            const { error: supaErr } = await supabaseClient
                .from('accounting_connections')
                .update({ status: CONNECTION_STATUS.DISCONNECTED, disconnected_at: new Date().toISOString() })
                .eq('id', connection.id);
            if (supaErr) throw supaErr;
            await loadConnections();
        } catch (err) {
            setActionError(err.message ?? 'Failed to disconnect.');
        }
    };

    const handleUpdateSyncSettings = async (connection, patch) => {
        if (!connection?.id) return;
        setActionError(null);
        setSuccessMessage(null);
        setSavingConnectionId(connection.id);

        const previous = {
            sync_entity_types: connection.sync_entity_types,
            sync_direction: connection.sync_direction,
            sync_paused: connection.sync_paused,
        };

        setConnections((prev) =>
            prev.map((row) => (row.id === connection.id ? { ...row, ...patch } : row))
        );

        try {
            const { error: supaErr } = await supabaseClient
                .from('accounting_connections')
                .update(patch)
                .eq('id', connection.id);
            if (supaErr) throw supaErr;
            setSuccessMessage(`${PLATFORM_LABELS[connection.platform]} sync settings updated.`);
        } catch (err) {
            setConnections((prev) =>
                prev.map((row) => (row.id === connection.id ? { ...row, ...previous } : row))
            );
            setActionError(err.message ?? 'Failed to update sync settings.');
        } finally {
            setSavingConnectionId(null);
        }
    };

    const handleSyncNow = async (connection) => {
        if (!connection?.id) return;
        setActionError(null);
        setSuccessMessage(null);
        setSyncingConnectionId(connection.id);

        try {
            const functionName = connection.platform === ACCOUNTING_PLATFORMS.QBO ? 'qbo-sync' : 'xero-sync';
            const syncEntityTypes = normalizeSyncEntityTypes(connection.sync_entity_types);
            const direction = connection.sync_direction || DEFAULT_SYNC_DIRECTION;

            const { data, error: invokeErr } = await supabaseClient.functions.invoke(functionName, {
                body: {
                    connectionId: connection.id,
                    direction,
                    entityTypes: syncEntityTypes,
                },
            });

            if (invokeErr) throw invokeErr;

            if (data?.paused) {
                setSuccessMessage(`${PLATFORM_LABELS[connection.platform]} sync is paused for this connection.`);
            } else {
                setSuccessMessage(`${PLATFORM_LABELS[connection.platform]} sync completed.`);
            }

            await loadConnections();
        } catch (err) {
            setActionError(err.message ?? 'Failed to run sync.');
        } finally {
            setSyncingConnectionId(null);
        }
    };

    // ── Status badge ─────────────────────────────────────────────────────────

    const statusBadge = (connection) => {
        if (!connection) return null;
        const badge = {
            [CONNECTION_STATUS.ACTIVE]: 'bg-green-100 text-green-800',
            [CONNECTION_STATUS.PENDING]: 'bg-yellow-100 text-yellow-800',
            [CONNECTION_STATUS.TOKEN_EXPIRED]: 'bg-orange-100 text-orange-800',
            [CONNECTION_STATUS.ERROR]: 'bg-red-100 text-red-800',
            [CONNECTION_STATUS.DISCONNECTED]: isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600',
        };
        const cls = badge[connection.status] ?? 'bg-gray-100 text-gray-600';
        return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
                {getConnectionStatusLabel(connection)}
            </span>
        );
    };

    // ── Render ───────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <span className={mutedClass}>Loading accounting connections…</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h2 className={`text-lg font-semibold ${labelClass}`}>Accounting Connections</h2>
                <p className={`text-sm mt-1 ${mutedClass}`}>
                    Connect IntelAgent to your accounting platform to keep invoices, payments,
                    and customers in sync automatically.
                </p>
            </div>

            {successMessage && (
                <div className="rounded-md bg-green-50 border border-green-200 p-4 text-sm text-green-800">
                    {successMessage}
                </div>
            )}

            {error && (
                <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">
                    {error}
                    <button
                        onClick={loadConnections}
                        className="ml-2 underline text-red-700 hover:text-red-900"
                    >
                        Retry
                    </button>
                </div>
            )}

            {actionError && (
                <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-800">
                    {actionError}
                </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {PLATFORMS.map((platform) => {
                    const connection = connectionForPlatform(platform.id);
                    const active = isConnectionActive(connection);
                    const needsReconnect = requiresReconnect(connection);
                    const syncEntityTypes = normalizeSyncEntityTypes(connection?.sync_entity_types);
                    const syncDirection = connection?.sync_direction || DEFAULT_SYNC_DIRECTION;
                    const syncPaused = Boolean(connection?.sync_paused);
                    const isSavingThisConnection = savingConnectionId === connection?.id;
                    const isSyncingThisConnection = syncingConnectionId === connection?.id;

                    return (
                        <div
                            key={platform.id}
                            className={`rounded-lg border p-5 flex flex-col gap-3 ${panelClass}`}
                            data-testid={`accounting-platform-card-${platform.id}`}
                        >
                            {/* Header */}
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <p className={`font-semibold ${labelClass}`}>{platform.label}</p>
                                    <p className={`text-xs mt-0.5 ${mutedClass}`}>{platform.description}</p>
                                </div>
                                {statusBadge(connection)}
                            </div>

                            {/* Last synced */}
                            {connection?.last_synced_at && (
                                <p className={`text-xs ${mutedClass}`}>
                                    Last synced: {new Date(connection.last_synced_at).toLocaleString()}
                                </p>
                            )}

                            {/* Sync error */}
                            {connection?.last_sync_error && (
                                <p className="text-xs text-red-600">
                                    Last error: {connection.last_sync_error}
                                </p>
                            )}

                            {/* Sync controls */}
                            {connection && connection.status !== CONNECTION_STATUS.DISCONNECTED && (
                                <div className="rounded-md border border-gray-200 p-3 space-y-3">
                                    <div>
                                        <p className={`text-xs font-medium ${labelClass}`}>Sync entity types</p>
                                        <div className="mt-2 flex flex-wrap gap-3 text-sm">
                                            {DEFAULT_SYNC_ENTITY_TYPES.map((entityType) => {
                                                const checked = syncEntityTypes.includes(entityType);
                                                const label = entityType === 'customer'
                                                    ? 'Customers'
                                                    : entityType === 'invoice'
                                                        ? 'Invoices'
                                                        : 'Payments';
                                                return (
                                                    <label key={entityType} className="inline-flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={(event) => {
                                                                const next = event.target.checked
                                                                    ? Array.from(new Set([...syncEntityTypes, entityType]))
                                                                    : syncEntityTypes.filter((value) => value !== entityType);
                                                                handleUpdateSyncSettings(connection, { sync_entity_types: next });
                                                            }}
                                                            disabled={isSavingThisConnection}
                                                        />
                                                        <span>{label}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <label htmlFor={`sync-direction-${platform.id}`} className={`text-xs font-medium ${labelClass}`}>
                                            Sync direction
                                        </label>
                                        <select
                                            id={`sync-direction-${platform.id}`}
                                            className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                                            value={syncDirection}
                                            onChange={(event) =>
                                                handleUpdateSyncSettings(connection, { sync_direction: event.target.value })
                                            }
                                            disabled={isSavingThisConnection}
                                        >
                                            <option value="both">Push + Pull</option>
                                            <option value="push">Push only</option>
                                            <option value="pull">Pull only</option>
                                        </select>
                                    </div>

                                    <label className="inline-flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={syncPaused}
                                            onChange={(event) =>
                                                handleUpdateSyncSettings(connection, { sync_paused: event.target.checked })
                                            }
                                            disabled={isSavingThisConnection}
                                        />
                                        <span>Pause sync for this connection</span>
                                    </label>
                                </div>
                            )}

                            {/* CTA */}
                            <div className="mt-auto pt-1 space-y-2">
                                {!connection || connection.status === CONNECTION_STATUS.DISCONNECTED ? (
                                    <button
                                        onClick={() => handleConnect(platform.id)}
                                        className="w-full rounded-md bg-blue-600 text-white text-sm font-medium py-2 px-4 hover:bg-blue-700 transition-colors"
                                    >
                                        Connect {platform.label}
                                    </button>
                                ) : needsReconnect ? (
                                    <button
                                        onClick={() => handleConnect(platform.id)}
                                        className="w-full rounded-md bg-orange-600 text-white text-sm font-medium py-2 px-4 hover:bg-orange-700 transition-colors"
                                    >
                                        Reconnect {platform.label}
                                    </button>
                                ) : active ? (
                                    <>
                                        <button
                                            onClick={() => handleSyncNow(connection)}
                                            className="w-full rounded-md bg-emerald-600 text-white text-sm font-medium py-2 px-4 hover:bg-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                            disabled={isSyncingThisConnection}
                                        >
                                            {isSyncingThisConnection ? 'Syncing…' : 'Sync now'}
                                        </button>
                                        <button
                                            onClick={() => handleDisconnect(connection)}
                                            className="w-full rounded-md border border-gray-300 text-sm font-medium py-2 px-4 hover:bg-gray-50 transition-colors"
                                        >
                                            Disconnect
                                        </button>
                                    </>
                                ) : (
                                    <button disabled className="w-full rounded-md border border-gray-200 text-sm py-2 px-4 opacity-50 cursor-not-allowed">
                                        Connecting…
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <p className={`text-xs ${mutedClass}`}>
                Both QuickBooks Online and Xero are first-class connectors with full two-way sync.
                Each platform maintains its own independent connection per organisation.
            </p>
        </div>
    );
}

export default AccountingConnectionsView;
